import {
  apiRequest,
  buildStrapiQueryString,
  getBackendBaseUrl,
  tokenStorage,
} from "./apiClient";
import type {
  StrapiListResponse,
  StrapiSingleResponse,
  Listing,
  TransportCompany,
  Pilot,
  FaqData,
  ContentPageData,
  AuthResponse,
  AuthUser,
  LoginCredentials,
  RegisterCredentials,
  TransportCompanyFilters,
  PilotFilters,
  ListingFilters,
  SubmittedApplication,
  Favorite,
  PilotPreference,
  StrapiFile,
} from "../types";

const EMPTY_PAGINATION = {
  page: 1,
  pageSize: 0,
  pageCount: 0,
  total: 0,
};

function emptyListResponse<T>(): StrapiListResponse<T> {
  return {
    data: [],
    meta: {
      pagination: EMPTY_PAGINATION,
    },
  };
}

// ============================================================
// AUTH API
// ============================================================

export async function loginUser(
  credentials: LoginCredentials,
): Promise<AuthResponse> {
  return apiRequest<AuthResponse>("/api/auth/local", {
    method: "POST",
    body: credentials,
  });
}

export async function registerUser(
  credentials: RegisterCredentials,
): Promise<AuthResponse> {
  return apiRequest<AuthResponse>("/api/auth/local/register", {
    method: "POST",
    body: credentials,
  });
}

export function getGoogleAuthUrl(): string {
  return `${getBackendBaseUrl()}/api/connect/google`;
}

export async function completeGoogleAuth(
  accessToken: string,
): Promise<AuthResponse> {
  return apiRequest<AuthResponse>(
    `/api/auth/google/callback?access_token=${encodeURIComponent(accessToken)}`,
  );
}

export async function getCurrentUser() {
  return apiRequest<AuthUser>("/api/qpro/me");
}

export async function updateUser(
  userId: number,
  data: Partial<AuthUser>,
): Promise<AuthUser> {
  void userId;
  return apiRequest<AuthUser>("/api/account/update-profile", {
    method: "PUT",
    body: { newEmail: data.email },
  });
}

export async function uploadFiles(files: File[]): Promise<StrapiFile[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const headers: HeadersInit = {};
  const authToken = tokenStorage.getToken();
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${getBackendBaseUrl()}/api/upload`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorBody = await response.json();
      message = errorBody?.error?.message || message;
    } catch {
      // Use the response status text when Strapi does not return JSON.
    }
    throw new Error(message);
  }

  return response.json();
}

// ============================================================
// FAQ API
// ============================================================

export async function fetchFaqData(): Promise<StrapiSingleResponse<FaqData>> {
  return apiRequest<StrapiSingleResponse<FaqData>>("/api/faq?populate=*");
}

// ============================================================
// CONTENT PAGES API
// ============================================================

export async function fetchTermsOfService(): Promise<
  StrapiSingleResponse<ContentPageData>
> {
  return apiRequest<StrapiSingleResponse<ContentPageData>>(
    "/api/terms-of-service",
  );
}

export async function fetchPrivacyPolicy(): Promise<
  StrapiSingleResponse<ContentPageData>
> {
  return apiRequest<StrapiSingleResponse<ContentPageData>>(
    "/api/privacy-policy",
  );
}

// ============================================================
// LISTINGS API
// ============================================================

export async function fetchListings(
  filters: ListingFilters = {},
): Promise<StrapiListResponse<Listing>> {
  const {
    searchQuery,
    dedication,
    permissions,
    page = 1,
    pageSize = 10,
    sortBy = "createdAt",
    sortOrder = "desc",
  } = filters;

  const queryParams: Record<
    string,
    string | number | boolean | string[] | undefined
  > = {
    page,
    pageSize,
    sort: `${sortBy}:${sortOrder}`,
  };

  if (searchQuery) {
    queryParams.searchQuery = searchQuery;
  }
  if (dedication) {
    queryParams.dedication = dedication;
  }
  if (permissions && permissions.length > 0) {
    queryParams.permissions = permissions;
  }

  const queryString = buildStrapiQueryString(queryParams);
  return apiRequest<StrapiListResponse<Listing>>(
    `/api/qpro/public/listings${queryString}`,
  );
}

export async function fetchListingById(
  listingId: string,
): Promise<StrapiSingleResponse<Listing>> {
  return apiRequest<StrapiSingleResponse<Listing>>(
    `/api/qpro/public/listings/${listingId}`,
  );
}

// ============================================================
// TRANSPORT COMPANIES API
// ============================================================

export async function fetchTransportCompanies(
  filters: TransportCompanyFilters = {},
): Promise<StrapiListResponse<TransportCompany>> {
  const {
    searchQuery,
    taxIdentificationNumber,
    city,
    verified,
    highlighted,
    page = 1,
    pageSize = 12,
    sortBy = "createdAt",
    sortOrder = "desc",
  } = filters;

  const queryParams: Record<
    string,
    string | number | boolean | string[] | undefined
  > = {
    page,
    pageSize,
    sort: `${sortBy}:${sortOrder}`,
  };

  if (searchQuery) {
    queryParams.searchQuery = searchQuery;
  }
  if (taxIdentificationNumber) {
    queryParams.taxIdentificationNumber = taxIdentificationNumber;
  }
  if (city) {
    queryParams.city = city;
  }
  if (verified !== undefined) {
    queryParams.verified = verified;
  }
  if (highlighted !== undefined) {
    queryParams.highlighted = highlighted;
  }

  const queryString = buildStrapiQueryString(queryParams);
  return apiRequest<StrapiListResponse<TransportCompany>>(
    `/api/qpro/public/transport-companies${queryString}`,
  );
}

export async function fetchTransportCompanyById(
  companyId: string,
): Promise<StrapiSingleResponse<TransportCompany>> {
  return apiRequest<StrapiSingleResponse<TransportCompany>>(
    `/api/qpro/public/transport-companies/${companyId}`,
  );
}

export async function fetchListingsByOwner(
  ownerId: number | string,
): Promise<StrapiListResponse<Listing>> {
  if (!String(ownerId).trim()) {
    return emptyListResponse<Listing>();
  }

  return apiRequest<StrapiListResponse<Listing>>(
    `/api/qpro/public/transport-companies/${ownerId}/listings`,
  );
}

export async function fetchListingsByOwner2(
  ownerId: number | string,
  listingStatus: "OPEN" | "CLOSED" | "CANCELED" = "OPEN",
): Promise<StrapiListResponse<Listing>> {
  if (!String(ownerId).trim()) {
    return emptyListResponse<Listing>();
  }

  const queryString = buildStrapiQueryString({ listingStatus });
  return apiRequest<StrapiListResponse<Listing>>(
    `/api/qpro/me/listings${queryString}`,
  );
}

// ============================================================
// PILOTS API
// ============================================================

export async function fetchPilots(
  filters: PilotFilters = {},
): Promise<StrapiListResponse<Pilot>> {
  const {
    searchQuery,
    pilotType,
    permissions,
    active = true,
    page = 1,
    pageSize = 12,
  } = filters;

  const queryParams: Record<
    string,
    string | number | boolean | string[] | undefined
  > = {
    active,
    page,
    pageSize,
  };

  if (searchQuery) {
    queryParams.searchQuery = searchQuery;
  }
  if (pilotType) {
    queryParams.pilotType = pilotType;
  }
  if (permissions && permissions.length > 0) {
    queryParams.permissions = permissions;
  }

  const queryString = buildStrapiQueryString(queryParams);
  return apiRequest<StrapiListResponse<Pilot>>(
    `/api/qpro/public/pilot-companies${queryString}`,
  );
}

export async function fetchPilotById(
  pilotCompanyId: string,
): Promise<StrapiSingleResponse<Pilot>> {
  return apiRequest<StrapiSingleResponse<Pilot>>(
    `/api/qpro/public/pilot-companies/${pilotCompanyId}`,
  );
}

// ============================================================
// APPLICATIONS API
// ============================================================

export async function submitApplication(
  applicationData: Omit<SubmittedApplication, "id">,
): Promise<StrapiSingleResponse<SubmittedApplication>> {
  const listingDocumentId = String(applicationData.listing ?? "");
  return apiRequest<StrapiSingleResponse<SubmittedApplication>>(
    `/api/qpro/listings/${listingDocumentId}/applications`,
    {
      method: "POST",
      body: {
        data: {
          content: applicationData.content,
          net_price: applicationData.net_price,
          tax: applicationData.tax,
        },
      },
    },
  );
}

export async function updateTransportCompany(
  documentId: string,
  companyData: any,
): Promise<StrapiSingleResponse<TransportCompany>> {
  void documentId;
  return apiRequest<StrapiSingleResponse<TransportCompany>>(
    "/api/qpro/me/transport-company",
    {
      method: "PATCH",
      body: { data: companyData },
    },
  );
}

export async function updatePilotProfile(
  documentId: string,
  pilotData: any,
): Promise<StrapiSingleResponse<Pilot>> {
  void documentId;
  return apiRequest<StrapiSingleResponse<Pilot>>("/api/qpro/me/pilot-company", {
    method: "PATCH",
    body: { data: pilotData },
  });
}

// ============================================================
// PILOT PREFERENCES API
// ============================================================

export async function fetchPilotPreferences(
  pilotId: number | string,
): Promise<StrapiListResponse<PilotPreference>> {
  return apiRequest<StrapiListResponse<PilotPreference>>(
    `/api/qpro/public/pilot-companies/${pilotId}/preferences`,
  );
}

export async function createPilotPreference(
  preferenceData: Partial<PilotPreference>,
): Promise<StrapiSingleResponse<PilotPreference>> {
  return apiRequest<StrapiSingleResponse<PilotPreference>>(
    "/api/qpro/me/pilot-preferences",
    {
      method: "POST",
      body: { data: preferenceData },
    },
  );
}

export async function deletePilotPreference(documentId: string): Promise<void> {
  return apiRequest<void>(`/api/qpro/me/pilot-preferences/${documentId}`, {
    method: "DELETE",
  });
}

// ============================================================
// FAVORITES API
// ============================================================

export async function fetchFavorites(): Promise<StrapiListResponse<Favorite>> {
  return apiRequest<StrapiListResponse<Favorite>>("/api/qpro/me/favorites");
}

export async function addFavorite(
  listingDocumentId: string,
): Promise<StrapiSingleResponse<Favorite>> {
  return apiRequest<StrapiSingleResponse<Favorite>>(
    `/api/qpro/me/favorites/${listingDocumentId}`,
    {
      method: "POST",
    },
  );
}

export async function removeFavorite(listingDocumentId: string): Promise<void> {
  return apiRequest<void>(`/api/qpro/me/favorites/${listingDocumentId}`, {
    method: "DELETE",
  });
}

// ============================================================
// MY PROFILE & LISTING CREATION API
// ============================================================

export async function createListing(
  listingData: any,
): Promise<StrapiSingleResponse<Listing>> {
  const safeListingData = { ...listingData };
  delete safeListingData.owner;
  return apiRequest<StrapiSingleResponse<Listing>>("/api/qpro/me/listings", {
    method: "POST",
    body: { data: safeListingData },
  });
}

export async function updateListing(
  documentId: string,
  listingData: any,
): Promise<StrapiSingleResponse<Listing>> {
  return apiRequest<StrapiSingleResponse<Listing>>(
    `/api/qpro/me/listings/${documentId}`,
    {
      method: "PATCH",
      body: { data: listingData },
    },
  );
}

export async function deleteListing(documentId: string): Promise<void> {
  return apiRequest<void>(`/api/qpro/me/listings/${documentId}`, {
    method: "DELETE",
  });
}

export async function fetchMyTransportCompany(
  userId: number,
): Promise<StrapiSingleResponse<TransportCompany>> {
  void userId;
  return apiRequest<StrapiSingleResponse<TransportCompany>>(
    "/api/qpro/me/transport-company",
  );
}

export async function fetchMyPilotProfile(
  userId: number,
): Promise<StrapiSingleResponse<Pilot>> {
  void userId;
  return apiRequest<StrapiSingleResponse<Pilot>>("/api/qpro/me/pilot-company");
}

export async function fetchMyApplications(): Promise<
  StrapiListResponse<SubmittedApplication>
> {
  return apiRequest<StrapiListResponse<SubmittedApplication>>(
    "/api/qpro/me/applications/sent",
  );
}

export async function fetchSentApplications(
  pilotId: string,
): Promise<StrapiListResponse<SubmittedApplication>> {
  void pilotId;
  return apiRequest<StrapiListResponse<SubmittedApplication>>(
    "/api/qpro/me/applications/sent",
  );
}

export async function fetchReceivedApplications(
  companyId: string,
  listingDocumentId?: string,
  listingStatus: "OPEN" | "CLOSED" | "CANCELED" = "OPEN",
): Promise<StrapiListResponse<SubmittedApplication>> {
  void companyId;
  const queryParams: Record<
    string,
    string | number | boolean | string[] | undefined
  > = {
    listingStatus,
  };

  if (listingDocumentId) {
    queryParams.listingDocumentId = listingDocumentId;
  }

  const queryString = buildStrapiQueryString(queryParams);
  return apiRequest<StrapiListResponse<SubmittedApplication>>(
    `/api/qpro/me/applications/received${queryString}`,
  );
}

export async function acceptApplication(
  listingDocumentId: string,
  applicationDocumentId: string,
): Promise<StrapiSingleResponse<SubmittedApplication>> {
  return apiRequest<StrapiSingleResponse<SubmittedApplication>>(
    `/api/qpro/listings/${listingDocumentId}/applications/${applicationDocumentId}/accept`,
    { method: "POST" },
  );
}

export async function sendApplicationMessage(
  applicationDocumentId: string,
  content: string,
): Promise<StrapiSingleResponse<SubmittedApplication>> {
  return apiRequest<StrapiSingleResponse<SubmittedApplication>>(
    `/api/qpro/applications/${applicationDocumentId}/messages`,
    {
      method: "POST",
      body: {
        content,
      },
    },
  );
}

export async function rejectApplication(
  listingDocumentId: string,
  applicationDocumentId: string,
): Promise<StrapiSingleResponse<SubmittedApplication>> {
  return apiRequest<StrapiSingleResponse<SubmittedApplication>>(
    `/api/qpro/listings/${listingDocumentId}/applications/${applicationDocumentId}/reject`,
    { method: "POST" },
  );
}

export async function startListing(
  listingDocumentId: string,
): Promise<StrapiSingleResponse<Listing>> {
  return apiRequest<StrapiSingleResponse<Listing>>(
    `/api/qpro/listings/${listingDocumentId}/start`,
    { method: "POST" },
  );
}

// ============================================================
// PROFILE CREATION API
// ============================================================

export async function createTransportCompany(
  companyData: any,
): Promise<StrapiSingleResponse<TransportCompany>> {
  return apiRequest<StrapiSingleResponse<TransportCompany>>(
    "/api/qpro/onboarding/transport-company",
    {
      method: "POST",
      body: { data: companyData },
    },
  );
}

export async function createPilotProfile(
  pilotData: any,
): Promise<StrapiSingleResponse<Pilot>> {
  return apiRequest<StrapiSingleResponse<Pilot>>(
    "/api/qpro/onboarding/pilot-company",
    {
      method: "POST",
      body: { data: pilotData },
    },
  );
}
